import { fmtMonto } from '@/lib/formatoGerencia';
import { CaretDown, MagnifyingGlass } from '@phosphor-icons/react';
import { useEffect, useMemo, useRef, useState } from 'react';

export interface PropsExcluidas {
  /** worksNo de las obras excluidas (compartido entre los informes por partida). */
  excluidas: Set<string>;
  onExcluidas: (nuevas: Set<string>) => void;
}

/** Mínimo que necesita el selector de cada fila (lo cumplen varios informes). */
export interface FilaObraBasica {
  worksNo: string;
  displayName: string | null;
  /** Partida (para contar cuántas partidas con monto tiene la obra). */
  taskNo: string;
  /** Monto de la fila (costo o M.O.): se suma por obra. */
  costo: number;
}

/** Columna/orden opcional por obra (p. ej. cobertura de horas en el informe por cuadrilla). */
export interface ExtraObra {
  titulo: string;
  texto: (worksNo: string) => string;
  /** Valor numérico para ordenar de menor a mayor. */
  valor: (worksNo: string) => number;
}

interface ObraInfo {
  worksNo: string;
  display: string | null;
  costo: number;
  /** Partidas distintas con costo: una obra con muy pocas suele ser una casa sin detalle por partida. */
  partidas: number;
}

type Orden = 'obra' | 'partidas' | 'costo' | 'extra';

/**
 * Lista las obras que entran al cálculo y permite excluir las que se quieran
 * (marcada = incluida). Pensado para sacar casas con información incompleta,
 * p. ej. entregadas cuando el costo aún no se llevaba por partida.
 */
export function SelectorObrasExcluidas({
  filas,
  excluidas,
  onChange,
  tituloMonto = 'Costo total',
  extra,
}: {
  filas: FilaObraBasica[];
  excluidas: Set<string>;
  onChange: (nuevas: Set<string>) => void;
  tituloMonto?: string;
  extra?: ExtraObra;
}) {
  const [abierto, setAbierto] = useState(false);
  const [busca, setBusca] = useState('');
  const [orden, setOrden] = useState<Orden>('obra');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierto) return;
    const fn = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [abierto]);

  const obras = useMemo(() => {
    const m = new Map<string, ObraInfo & { tareas: Set<string> }>();
    for (const f of filas) {
      let o = m.get(f.worksNo);
      if (!o) {
        o = {
          worksNo: f.worksNo,
          display: f.displayName,
          costo: 0,
          partidas: 0,
          tareas: new Set(),
        };
        m.set(f.worksNo, o);
      }
      o.costo += f.costo;
      if (f.costo !== 0) o.tareas.add(f.taskNo);
    }
    return [...m.values()].map((o) => ({ ...o, partidas: o.tareas.size }));
  }, [filas]);

  const visibles = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const lista = q
      ? obras.filter(
          (o) => o.worksNo.toLowerCase().includes(q) || (o.display ?? '').toLowerCase().includes(q),
        )
      : [...obras];
    const porNombre = (a: ObraInfo, b: ObraInfo) =>
      a.worksNo.localeCompare(b.worksNo, 'es', { numeric: true });
    lista.sort((a, b) =>
      orden === 'partidas'
        ? a.partidas - b.partidas || porNombre(a, b)
        : orden === 'costo'
          ? b.costo - a.costo
          : orden === 'extra' && extra
            ? extra.valor(a.worksNo) - extra.valor(b.worksNo) || porNombre(a, b)
            : porNombre(a, b),
    );
    return lista;
  }, [obras, busca, orden, extra]);

  const nExcluidas = obras.filter((o) => excluidas.has(o.worksNo)).length;
  const nIncluidas = obras.length - nExcluidas;

  const toggle = (worksNo: string) => {
    const next = new Set(excluidas);
    if (next.has(worksNo)) next.delete(worksNo);
    else next.add(worksNo);
    onChange(next);
  };
  const excluirVisibles = () => {
    const next = new Set(excluidas);
    for (const o of visibles) next.add(o.worksNo);
    onChange(next);
  };
  const incluirVisibles = () => {
    const next = new Set(excluidas);
    for (const o of visibles) next.delete(o.worksNo);
    onChange(next);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 ${
          nExcluidas > 0
            ? 'border-amber-400 bg-amber-50 text-amber-900'
            : 'border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50'
        }`}
      >
        Obras: {nIncluidas} de {obras.length}
        {nExcluidas > 0 && <span className="font-semibold">({nExcluidas} excluidas)</span>}
        <CaretDown className="h-3 w-3" weight="bold" />
      </button>

      {abierto && (
        <div className="absolute left-0 z-50 mt-1 w-[640px] max-w-[92vw] rounded-md border border-zinc-300 bg-white p-3 shadow-lg">
          <p className="mb-2 text-[11px] text-muted-foreground">
            Estas son las obras que se toman en cuenta. Desmarcá las que quieras excluir (por
            ejemplo, entregadas sin detalle por partida): los totales, razones y exportaciones se
            recalculan sin ellas. «Partidas» cuenta las partidas con costo de la obra.
          </p>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <div className="relative min-w-[160px] flex-1">
              <MagnifyingGlass className="absolute left-2 top-1.5 h-3.5 w-3.5 text-zinc-400" />
              <input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar obra o modelo…"
                className="w-full rounded border border-zinc-300 py-1 pl-7 pr-2"
              />
            </div>
            <label className="flex items-center gap-1 text-muted-foreground">
              Ordenar:
              <select
                value={orden}
                onChange={(e) => setOrden(e.target.value as Orden)}
                className="rounded border border-zinc-300 bg-white px-1 py-1 text-zinc-800"
              >
                <option value="obra">Obra</option>
                <option value="partidas">Menos partidas primero</option>
                <option value="costo">Mayor monto primero</option>
                {extra && <option value="extra">Menor {extra.titulo.toLowerCase()} primero</option>}
              </select>
            </label>
          </div>
          <div className="mb-2 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={incluirVisibles}
              className="text-blue-600 hover:underline"
            >
              Incluir {busca.trim() ? 'las visibles' : 'todas'}
            </button>
            <button
              type="button"
              onClick={excluirVisibles}
              className="text-blue-600 hover:underline"
            >
              Excluir {busca.trim() ? 'las visibles' : 'todas'}
            </button>
            {nExcluidas > 0 && (
              <button
                type="button"
                onClick={() => onChange(new Set())}
                className="ml-auto text-red-600 hover:underline"
              >
                Restablecer (incluir todas)
              </button>
            )}
          </div>
          <div className="max-h-[360px] overflow-auto rounded border border-zinc-200">
            <table className="w-full">
              <thead className="sticky top-0 bg-zinc-100 text-left text-[11px] text-zinc-600">
                <tr>
                  <th className="w-8 px-2 py-1" />
                  <th className="px-2 py-1">Obra</th>
                  <th className="px-2 py-1 text-right">Partidas</th>
                  {extra && <th className="px-2 py-1 text-right">{extra.titulo}</th>}
                  <th className="px-2 py-1 text-right">{tituloMonto}</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((o) => {
                  const incluida = !excluidas.has(o.worksNo);
                  return (
                    <tr
                      key={o.worksNo}
                      className={`border-t border-zinc-100 hover:bg-zinc-50 ${
                        incluida ? '' : 'bg-amber-50/60 text-zinc-500'
                      }`}
                    >
                      <td className="px-2 py-1">
                        <input
                          id={`chk-${o.worksNo}`}
                          type="checkbox"
                          checked={incluida}
                          onChange={() => toggle(o.worksNo)}
                          aria-label={`Incluir ${o.worksNo}`}
                        />
                      </td>
                      <td className="p-0">
                        <label
                          htmlFor={`chk-${o.worksNo}`}
                          className="block cursor-pointer px-2 py-1"
                        >
                          <span className="font-medium">{o.worksNo}</span>
                          {o.display && o.display !== o.worksNo && (
                            <span className="ml-1 text-[11px] text-muted-foreground">
                              · {o.display}
                            </span>
                          )}
                        </label>
                      </td>
                      <td className="p-0 text-right tabular-nums">
                        <label
                          htmlFor={`chk-${o.worksNo}`}
                          className="block cursor-pointer px-2 py-1"
                        >
                          {o.partidas}
                        </label>
                      </td>
                      {extra && (
                        <td className="p-0 text-right tabular-nums">
                          <label
                            htmlFor={`chk-${o.worksNo}`}
                            className="block cursor-pointer px-2 py-1"
                          >
                            {extra.texto(o.worksNo)}
                          </label>
                        </td>
                      )}
                      <td className="p-0 text-right tabular-nums">
                        <label
                          htmlFor={`chk-${o.worksNo}`}
                          className="block cursor-pointer px-2 py-1"
                        >
                          {fmtMonto(o.costo)}
                        </label>
                      </td>
                    </tr>
                  );
                })}
                {visibles.length === 0 && (
                  <tr>
                    <td
                      colSpan={extra ? 5 : 4}
                      className="px-2 py-4 text-center text-muted-foreground"
                    >
                      Sin resultados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-2 text-[11px] text-muted-foreground">
            {nIncluidas} incluidas · {nExcluidas} excluidas · se recuerda en este navegador
          </div>
        </div>
      )}
    </div>
  );
}
